import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Statistic, Row, Col, Button, message, Spin } from 'antd';
import { WalletOutlined, ArrowUpOutlined, ArrowDownOutlined, CalendarOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pointsApi } from '../services/api';

const TransactionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  useEffect(() => {
    loadTransactions();
    loadStatistics();
  }, []);

  const loadTransactions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await pointsApi.getTransactions({ page, per_page: pagination.pageSize });
      setTransactions(response.data.transactions || []);
      setPagination(prev => ({
        ...prev,
        current: response.data.page,
        total: response.data.total
      }));
    } catch (error) {
      message.error('加载交易记录失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await pointsApi.getStatistics();
      setStatistics(response.data.statistics || {});
    } catch (error) {
      console.error('加载统计数据失败:', error);
    }
  };

  const handleTableChange = (pagination) => {
    loadTransactions(pagination.current);
  };

  const columns = [
    {
      title: '日期',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => (
        <span style={{ color: '#a0a0b0' }}>
          {text ? new Date(text).toLocaleString() : '-'}
        </span>
      )
    },
    {
      title: '类型',
      dataIndex: 'type_name',
      key: 'type_name',
      width: 100,
      render: (text, record) => {
        const color = record.type === 'income' || record.type === 'recharge' || record.type === 'award' ? 'green' : 'red';
        return (
          <Tag color={color} style={{ fontWeight: 'bold' }}>
            {text}
          </Tag>
        );
      }
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (text) => (
        <span style={{ 
          fontSize: 16, 
          fontWeight: 'bold',
          color: text > 0 ? '#00d4aa' : '#ff4d4f'
        }}>
          {text > 0 ? '+' : ''}{text} 积分
        </span>
      )
    },
    {
      title: '余额',
      dataIndex: 'balance_after',
      key: 'balance_after',
      width: 100,
      render: (text) => (
        <span style={{ color: '#e8e8ec' }}>{text} 积分</span>
      )
    },
    {
      title: '关联订单',
      dataIndex: 'order_title',
      key: 'order_title',
      width: 200,
      render: (text) => (
        <span style={{ color: '#a0a0b0' }}>{text || '-'}</span>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text) => (
        <span style={{ color: '#a0a0b0' }}>{text || '-'}</span>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button 
        icon={<ArrowDownOutlined />} 
        onClick={() => navigate('/profile')}
        style={{ marginBottom: 24, background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec' }}
      >
        返回个人中心
      </Button>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={6}>
          <Card style={{ background: '#1a1a24', border: '1px solid #2a2a3a' }}>
            <Statistic
              title="当前余额"
              value={statistics.current_balance || user?.points || 0}
              prefix={<WalletOutlined />}
              valueStyle={{ color: '#00d4aa' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={{ background: '#1a1a24', border: '1px solid #2a2a3a' }}>
            <Statistic
              title="总收入"
              value={statistics.total_income || 0}
              prefix={<ArrowUpOutlined />}
              valueStyle={{ color: '#00d4aa' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={{ background: '#1a1a24', border: '1px solid #2a2a3a' }}>
            <Statistic
              title="总支出"
              value={statistics.total_expense || 0}
              prefix={<ArrowDownOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={{ background: '#1a1a24', border: '1px solid #2a2a3a' }}>
            <Statistic
              title="总充值"
              value={statistics.total_recharge || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#ffd700' }}
            />
          </Card>
        </Col>
      </Row>

      <Card 
        style={{ background: '#1a1a24', border: '1px solid #2a2a3a' }}
        title={<span style={{ color: '#e8e8ec' }}>
          <FileTextOutlined /> 交易记录
        </span>}
      >
        <Spin spinning={loading}>
          <Table
            dataSource={transactions}
            columns={columns}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: '暂无交易记录'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default TransactionPage;
